import OfferExternalStatus from '../components/OfferExternalStatus';
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { offersAPI, ficAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CloudArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import ImportFICQuotesModal from '../components/ImportFICQuotesModal';
import StatoEvasioneBadge from '../components/StatoEvasioneBadge';
import OfferStatusBadge from '../components/OfferStatusBadge';
import ConfirmModal from '../components/ConfirmModal';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/constants';

const Offers: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('stato') || '');
  const [as400Status, setAs400Status] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [rejectModalOffer, setRejectModalOffer] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const limit = 20;

  const canApproveDiscount = user?.permissions?.includes('offerte:approve_discount') || user?.is_host;
  const canCreateOffer = hasPermission('offerte:create');
  const canDeleteOffer = hasPermission('offerte:delete');

  const { data, isLoading } = useQuery({
    queryKey: ['offers', page, search, status, approvalFilter, as400Status],
    queryFn: () => {
      if (approvalFilter === 'pending_approval') {
        return offersAPI.getPendingApproval({ page, limit, as400_status: as400Status || undefined });
      }
      const params: any = { page, limit, search, stato: status, as400_status: as400Status || undefined };
      if (approvalFilter === 'ordini_clienti') {
        params.origine = 'cliente';
      }
      if (approvalFilter === 'my_clients') {
        params.my_clients = true;
      }
      return offersAPI.getOffers(params);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (offerId: string) => offersAPI.approveDiscount(offerId),
    onSuccess: () => {
      toast.success('Sconti approvati con successo');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'approvazione');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ offerId, note }: { offerId: string; note?: string }) => offersAPI.rejectDiscount(offerId, note),
    onSuccess: () => {
      toast.success('Sconti rifiutati');
      setRejectModalOffer(null);
      setRejectNote('');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il rifiuto');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (offerId: string) => offersAPI.deleteOffer(offerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success('Offerta eliminata');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    },
  });

  // Check FIC connection status
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: async () => {
      const response = await ficAPI.getStatus();
      return response.data;
    },
  });

  const offers = data?.data?.data || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Offerte</h1>
          <p className="mt-1 text-sm text-gray-500">Gestisci le tue offerte commerciali</p>
        </div>
        <div className="flex gap-3">
          {ficStatus?.connected && (
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <CloudArrowDownIcon className="h-4 w-4 mr-2" />
              Importa da FIC
            </button>
          )}
          {canCreateOffer && (
            <Link
              to="/offers/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Nuova Offerta
            </Link>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Cerca offerte..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                aria-label="Cerca offerte"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select aria-label="Invio al sistema esterno" value={as400Status} onChange={e => { setAs400Status(e.target.value); setPage(1); }} className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-primary-500 focus:ring-primary-500">
              <option value="">Invio: tutte</option>
              <option value="sent">Inviate</option>
              <option value="not_sent">Da inviare</option>
            </select>
            <select
              aria-label="Filtra offerte"
              value={approvalFilter || status}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'pending_approval' || val === 'ordini_clienti' || val === 'my_clients') {
                  setApprovalFilter(val);
                  setStatus('');
                } else {
                  setApprovalFilter('');
                  setStatus(val);
                }
                setPage(1);
              }}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="">Tutti gli stati</option>
              <option value="bozza">Bozza (tutte)</option>
              <option value="in_validazione">In validazione</option>
              <option value="sconti_da_validare">Sconti da validare</option>
              <option value="validata">Validata</option>
              <option value="inviata">Inviata</option>
              <option value="accettata">Accettata</option>
              <option value="rifiutata">Rifiutata</option>
              <option value="scaduta">Scaduta</option>
              <option value="ordini_clienti">Ordini Clienti</option>
              <option value="my_clients">I miei clienti</option>
              {canApproveDiscount && (
                <option value="pending_approval">In attesa approvazione</option>
              )}
            </select>
            
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filtra
            </button>
          </div>
        </form>
      </div>

      {/* Offers List */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : offers.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna offerta trovata</h3>
            <p className="text-gray-500 mb-4">
              {search || status 
                ? 'Prova a modificare i filtri di ricerca' 
                : 'Crea la tua prima offerta per iniziare'
              }
            </p>
            {canCreateOffer && (
              <Link
                to="/offers/new"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Crea Offerta
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Elenco offerte">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                <tr>{['Documento', 'Data', 'Cliente', 'Stato', 'Scadenza', 'Totale', 'Azioni'].map(label => <th key={label} scope="col" className={`px-4 py-3 ${label === 'Totale' || label === 'Azioni' ? 'text-right' : ''}`}>{label}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offers.map(offer => (
                  <tr key={offer.id} className="cursor-pointer hover:bg-gray-50" onClick={event => {
                    if ((event.target as HTMLElement).closest('a, button, input, select, textarea, [tabindex]')) return;
                    navigate(`/offers/${offer.id}/edit`);
                  }}>
                    <td className="px-4 py-3"><Link to={`/offers/${offer.id}/edit`} aria-label={`Apri offerta ${offer.numero}`} className="font-semibold text-primary-700 hover:underline">{offer.numero}</Link><p className="mt-0.5 line-clamp-2 min-w-40 max-w-xs text-xs text-gray-500">{offer.titolo}</p></td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600 tabular-nums">{new Date(offer.created_at).toLocaleDateString('it-IT')}</td>
                    <td className="min-w-40 px-4 py-3 font-medium text-gray-900">{offer.cliente.nome}</td>
                    <td className="px-4 py-3"><div className="flex min-w-40 flex-wrap items-center gap-1.5">
                      <OfferStatusBadge stato={offer.stato} commerciale={offer.stato_commerciale} />
                      <OfferExternalStatus offerId={offer.id} />
                      {offer.origine === 'cliente' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Cliente
                        </span>
                      )}
                      {offer.approvazione_richiesta && offer.approvazione_stato === 'pending' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Approvazione sconti
                        </span>
                      )}
                      {offer.approvazione_richiesta && offer.approvazione_stato === 'approved' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Sconti approvati
                        </span>
                      )}
                      {offer.approvazione_richiesta && offer.approvazione_stato === 'rejected' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Sconti rifiutati
                        </span>
                      )}
                      <StatoEvasioneBadge statoEvasione={offer.stato_evasione} wmsOrderId={offer.wms_order_id} />
                    </div></td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600 tabular-nums">{offer.scadenza ? new Date(offer.scadenza).toLocaleDateString('it-IT') : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums">{formatCurrency(offer.importo_totale)}</td>
                    <td className="px-4 py-3"><div className="flex items-center justify-end gap-2">
                    {canApproveDiscount && offer.approvazione_richiesta && offer.approvazione_stato === 'pending' && (
                      <>
                        <button
                          onClick={(e) => { e.preventDefault(); approveMutation.mutate(offer.id); }}
                          disabled={approveMutation.isPending}
                          className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          title="Approva sconti"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Approva
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); setRejectModalOffer(offer.id); }}
                          className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          title="Rifiuta sconti"
                        >
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Rifiuta
                        </button>
                      </>
                    )}
                    {offer.stato === 'bozza' && canDeleteOffer && (
                      <button
                        onClick={(e) => { e.preventDefault(); setDeleteTarget({ id: offer.id, label: `${offer.numero} - ${offer.titolo}` }); }}
                        className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Elimina offerta"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}

                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Precedente
            </button>
            <button
              onClick={() => setPage(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Successivo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando da <span className="font-medium">{(page - 1) * limit + 1}</span> a{' '}
                <span className="font-medium">{Math.min(page * limit, pagination.total)}</span> di{' '}
                <span className="font-medium">{pagination.total}</span> risultati
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Precedente
                </button>
                {[...Array(pagination.pages)].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === page
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page >= pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Successivo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Import FIC Quotes Modal */}
      <ImportFICQuotesModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />

      {/* Reject Discount Approval Modal */}
      {rejectModalOffer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => { setRejectModalOffer(null); setRejectNote(''); }} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rifiuta approvazione sconti</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivazione *
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  rows={3}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Inserisci il motivo del rifiuto..."
                />
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => { setRejectModalOffer(null); setRejectNote(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (rejectNote.trim()) {
                      rejectMutation.mutate({ offerId: rejectModalOffer, note: rejectNote });
                    }
                  }}
                  disabled={!rejectNote.trim() || rejectMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectMutation.isPending ? 'Rifiutando...' : 'Rifiuta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina offerta"
          message={`Sei sicuro di voler eliminare l'offerta "${deleteTarget.label}"? Questa operazione è reversibile (soft delete).`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
};

export default Offers;